import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export type FirestoreCollectionInfo = {
  id: string;
  path: string;
};

export type FirestoreDocumentListItem = {
  id: string;
  path: string;
  preview: string;
};

export type FirestoreDocumentDetail = {
  id: string;
  path: string;
  data: Record<string, unknown>;
};

type BrowserAction =
  | { action: 'listRootCollections' }
  | { action: 'listDocuments'; collectionPath: string; pageSize?: number; startAfterId?: string }
  | { action: 'searchDocuments'; collectionPath: string; query: string; pageSize?: number }
  | { action: 'listSubcollections'; documentPath: string }
  | { action: 'getDocument'; documentPath: string }
  | { action: 'deleteDocument'; documentPath: string; confirmPath: string }
  | { action: 'deleteCollection'; collectionPath: string; confirmPath: string };

const browserFn = httpsCallable<BrowserAction, unknown>(functions, 'adminFirestoreBrowser');

async function callBrowser<T>(payload: BrowserAction): Promise<T> {
  const result = await browserFn(payload);
  return result.data as T;
}

export const adminFirestoreBrowserService = {
  listRootCollections: () =>
    callBrowser<{ collections: FirestoreCollectionInfo[] }>({ action: 'listRootCollections' }),

  listDocuments: (collectionPath: string, options?: { pageSize?: number; startAfterId?: string }) =>
    callBrowser<{
      collectionPath: string;
      documents: FirestoreDocumentListItem[];
      hasMore: boolean;
      lastId: string | null;
    }>({
      action: 'listDocuments',
      collectionPath,
      pageSize: options?.pageSize,
      startAfterId: options?.startAfterId,
    }),

  searchDocuments: (collectionPath: string, query: string, pageSize?: number) =>
    callBrowser<{ collectionPath: string; documents: FirestoreDocumentListItem[] }>({
      action: 'searchDocuments',
      collectionPath,
      query,
      pageSize,
    }),

  listSubcollections: (documentPath: string) =>
    callBrowser<{ documentPath: string; subcollections: FirestoreCollectionInfo[] }>({
      action: 'listSubcollections',
      documentPath,
    }),

  getDocument: (documentPath: string) =>
    callBrowser<FirestoreDocumentDetail>({ action: 'getDocument', documentPath }),

  deleteDocument: (documentPath: string, confirmPath: string) =>
    callBrowser<{ success: boolean; path: string; deletedDocuments: number }>({
      action: 'deleteDocument',
      documentPath,
      confirmPath,
    }),

  deleteCollection: (collectionPath: string, confirmPath: string) =>
    callBrowser<{ success: boolean; path: string; deletedDocuments: number }>({
      action: 'deleteCollection',
      collectionPath,
      confirmPath,
    }),
};
